export class KeyCoalescingMap<K, T> implements Map<K, T> {
  private readonly map: Map<K, T>;
  private readonly keyConversionMap: Map<K, K>; // lookup key -> original key

  constructor(
    // Function provided by the consumer that tells the class how to standardize the key.
    private readonly keyModulator: (key: K) => K,
    // Initial key/value pairs in th map. Mimics the Map constructor.
    entries?: readonly (readonly [K, T])[],
  ) {
    // Instantiate our private maps with the entries, if they were provided. But convert the keys before doing so.
    this.map = new Map<K, T>(entries && entries.map(([k, t]) => [this.keyModulator(k), t]));
    this.keyConversionMap = new Map<K, K>(
      entries && entries.map(([k, t]) => [this.keyModulator(k), k]),
    );
  }
  clear(): void {
    this.map.clear();
    this.keyConversionMap.clear();
  }
  delete(key: K): boolean {
    // We have to use the standardized key to look up entries in our internal maps.
    this.keyConversionMap.delete(this.keyModulator(key));
    return this.map.delete(this.keyModulator(key));
  }
  forEach(callbackfn: (value: T, key: K, map: Map<K, T>) => void, thisArg?: any): void {
    // calbackfn expects a map as the third param. So we have to create a tempMap that is keyed on the original key.
    // That way the consumer never sees the implementation (like if we just returned this.map as that param).
    const tempMap = new Map<K, T>(
      [...this.map].map(([key, val]) => {
        const originalKey = this.keyConversionMap.get(key);
        return [originalKey, val];
      }),
    );
    // Since we have a tempMap keyed on original key, this becomes just a passthrough.
    tempMap.forEach((v, k, m) => callbackfn(v, k, m), thisArg);
  }
  get(key: K): T | undefined {
    return this.map.get(this.keyModulator(key));
  }
  has(key: K): boolean {
    return this.map.has(this.keyModulator(key));
  }
  set(key: K, value: T): this {
    this.map.set(this.keyModulator(key), value);
    // We have to store the mapping from lookup key to original key so we can work backwards later.
    this.keyConversionMap.set(this.keyModulator(key), key);
    return this;
  }
  get size(): number {
    return this.map.size;
  }
  *[Symbol.iterator](): IterableIterator<[K, T]> {
    // This function does the same thing as entries().
    yield* this.entries();
  }
  *entries(): IterableIterator<[K, T]> {
    // For each iteration, we need to return an [originalkey, value] pair so that the consumer doesn't see the implementation.
    // So we iterate over this.map and convert its key back to the original provided by the consumer.
    for (let [key, val] of this.map) {
      const originalKey = this.keyConversionMap.get(key);
      yield [originalKey, val];
    }
  }
  *keys(): IterableIterator<K> {
    // Since entries already does the work of converting the key, we can use it and return just the key part.
    for (let [key] of this.entries()) {
      yield key;
    }
  }
  *values(): IterableIterator<T> {
    yield* this.map.values();
  }
  get [Symbol.toStringTag](): string {
    return this.map[Symbol.toStringTag];
  }
}

// KeyCoalescingMap is just as easy to define generically as it is to specifically implement a CaseInsensitiveMap.
// So to separate concerns as best as possible, CaseInsensitiveMap is a KeyCoalescingMap that just defines the standardization function as toLower.
// Now, the hardest amount of work is done in the KeyCoalescingMap and the CaseInsensitiveMap is only responsible for choosing the standardization function.
export class CaseInsensitiveMap<T> extends KeyCoalescingMap<string, T> {
  constructor(entries?: readonly (readonly [string, T])[]) {
    super(s => s.toLowerCase(), entries);
  }
}
